import React from 'react';
import { useNotification } from '../../context/NotificationContext';
import { notificationModel } from '../../data/notificationModel';
import { useAuth } from '../../context/AuthContext';

const NotificationItem = ({ notification, onClose }) => {
  const { markAsRead, deleteNotification } = useNotification();
  const { currentUser } = useAuth();
  const isHandlingClick = React.useRef(false);

  // Get navigation path based on notification type
  const getNavigationPath = () => {
    // Video call notifications
    if (notification.type === 'video_call_request') {
      return '/marketplace/my-store?tab=chat&subTab=videoCallRequests';
    }
    if (notification.type === 'video_call_incoming' && notification.data?.callId) {
      // Navigate to video call page
      if (notification.data.chatId) {
        return `/marketplace/video-call/${notification.data.chatId}`;
      } else if (notification.data.productId && notification.data.storeId) {
        return `/marketplace/video-call/product/${notification.data.productId}/store/${notification.data.storeId}`;
      } else {
        return '/marketplace/my-video-calls';
      }
    }
    if (notification.type === 'video_call_reminder' && notification.data?.action === 'join_call' && notification.data?.requestId) {
      return `/marketplace/video-call/request/${notification.data.requestId}`;
    }
    if (notification.type === 'video_call_approved' || notification.type === 'video_call_rejected' || notification.type === 'video_call_reminder') {
      return '/marketplace/my-video-calls';
    }
    
    // Other notification types
    if (notification.data?.orderId) {
      return `/marketplace/orders/${notification.data.orderId}`;
    }
    if (notification.data?.productId) {
      return `/marketplace/products/${notification.data.productId}`;
    }
    if (notification.data?.storeId) {
      return `/marketplace/stores/${notification.data.storeId}`;
    }
    if (notification.data?.auctionId) {
      return `/marketplace/auctions/${notification.data.auctionId}`;
    }
    if (notification.data?.chatId) {
      return `/marketplace/chat/${notification.data.chatId}`;
    }
    
    return null;
  };

  const handleClick = async (e) => {
    // Prevent duplicate calls
    if (isHandlingClick.current) {
      return;
    }
    
    isHandlingClick.current = true;
    
    // Stop event propagation
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    try {
      // Mark as read first (don't await if there's navigation)
      if (!notification.read) {
        const notificationId = notification.id || notification._id;
        if (notificationId && typeof notificationId === 'string' && notificationId.trim() !== '') {
          try {
            console.log('[NotificationItem] Marking notification as read:', notificationId);
            // Don't await - mark as read in background, but don't block navigation
            markAsRead(notificationId).catch(markError => {
              console.error('[NotificationItem] Error marking notification as read:', markError);
            });
          } catch (markError) {
            console.error('[NotificationItem] Error marking notification as read:', markError);
          }
        } else {
          console.warn('[NotificationItem] Invalid notification ID:', notificationId);
        }
      }

      // Get navigation path
      const targetPath = getNavigationPath();
      
      if (targetPath) {
        // Close dropdown before navigation
        if (onClose) {
          onClose();
        }
        
        // Use window.location for reliable navigation (bypasses React Router issues)
        setTimeout(() => {
          window.location.href = targetPath;
        }, 50);
      } else {
        // If no navigation path, just mark as read and keep dropdown open
        // Wait a bit for mark as read to complete
        if (!notification.read) {
          const notificationId = notification.id || notification._id;
          if (notificationId) {
            try {
              await markAsRead(notificationId);
              // Don't close dropdown - let user see the updated state
            } catch (markError) {
              console.error('[NotificationItem] Error marking notification as read:', markError);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      
      // Close dropdown even if there's an error (only if navigating)
      const targetPath = getNavigationPath();
      if (targetPath && onClose) {
        onClose();
        setTimeout(() => {
          window.location.href = targetPath;
        }, 50);
      }
    } finally {
      // Reset flag
      setTimeout(() => {
        isHandlingClick.current = false;
      }, 500);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    const notificationId = notification.id || notification._id;
    if (!notificationId) {
      console.error('Notification ID is missing:', notification);
      alert('ไม่สามารถลบการแจ้งเตือนได้: ไม่พบ ID');
      return;
    }
    if (window.confirm('ต้องการลบการแจ้งเตือนนี้?')) {
      try {
        await deleteNotification(notificationId);
      } catch (error) {
        console.error('Error deleting notification:', error);
        alert('เกิดข้อผิดพลาดในการลบการแจ้งเตือน');
      }
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'เมื่อสักครู่';
    if (minutes < 60) return `${minutes} นาทีที่แล้ว`;
    if (hours < 24) return `${hours} ชั่วโมงที่แล้ว`;
    if (days < 7) return `${days} วันที่แล้ว`;
    return date.toLocaleDateString('th-TH');
  };

  const icon = notificationModel.icons[notification.type] || '🔔';

  const targetPath = getNavigationPath();

  return (
    <div
      onClick={handleClick}
      onMouseDown={(e) => {
        // Prevent backdrop from intercepting
        e.stopPropagation();
      }}
      className={`notification-item p-3 sm:p-4 border-b border-gray-100 active:bg-gray-100 cursor-pointer transition-colors relative ${
        !notification.read ? 'bg-blue-50 md:bg-blue-50' : 'bg-white'
      }`}
      style={{ pointerEvents: 'auto', zIndex: 10, position: 'relative' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick(e);
        }
      }}
    >
      <div className="flex items-start gap-3">
        <div className="text-2xl sm:text-3xl flex-shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h4 className={`text-sm sm:text-base font-semibold flex-1 ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                  {notification.title}
                </h4>
                {!notification.read && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                )}
              </div>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed break-words">
                {notification.message}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mt-2">
                {formatTime(notification.createdAt)}
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="text-gray-400 hover:text-red-500 active:text-red-600 transition-colors flex-shrink-0 p-1"
          aria-label="ลบ"
        >
          <svg className="w-5 h-5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NotificationItem;


